import { Assignment } from '@mui/icons-material';
import dayjs from 'dayjs';
import { sum } from 'lodash-es';
import { useRecoilValue } from 'recoil';

import { ACLOSET_ORDER_STATUS_SHORT_CODE_MAP } from '@/MetadataType';
import type { OrderCountsRequest } from '@/app/orders/common/type';
import useGetOrderCounts from '@/app/orders/hooks/useGetOrderCounts';
import useGetShopInfo from '@/app/shop/shop-info/useGetShopInfo';
import { basicRoles } from '@/router/consts';
import { orderMap } from '@/router/orderMap';
import { shopAuthContextState, useShopAuth } from '@/state/userAuthContext';
import { addTimezoneToDate } from '@/utils/dateUtil';

import type { SidebarMenu } from '../../types';

const 상태_값_매핑 = {
  'payment-pending': ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['payment-pending'], // 미결제 확인(결제 대기)
  'payment-done': ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['payment-done'], // 상품 준비중 관리(결제 완료)
  'shipping-pending': ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['shipping-pending'], // 배송 준비중 관리
  'shipping-in-progress': ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['shipping-in-progress'], // 배송중 관리
  'shipping-done': ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['shipping-done'], // 배송 완료 관리
  'purchase-done': ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['purchase-done'], // 구매 확정 내역
};

/**
 *  사이드바 > 주문관리 메뉴
 */
export default function useOrderMenus() {
  const shopAuth = useShopAuth();
  const shopID = shopAuth.shopID;

  const { data: shopInfo } = useGetShopInfo(shopID);

  const orderMenus: SidebarMenu[] = [
    {
      title: orderMap.index.title,
      path: orderMap.index.path,
      icon: <Assignment />,
      roles: basicRoles,
      childrenMenus: [
        {
          title: orderMap['integratedOrder'].title,
          path: orderMap['integratedOrder'].path,
          display: orderMap['integratedOrder'].display,
        },
        {
          title: orderMap['duplicatedOrder'].title,
          path: orderMap['duplicatedOrder'].path,
          display: orderMap['duplicatedOrder'].display,
          count: useGetDuplicatedOrderCounts(),
        },
        {
          title: orderMap['payment-pending'].title,
          path: orderMap['payment-pending'].path,
          display: orderMap['payment-pending'].display,
          count: useGetFilteredOrderCounts(상태_값_매핑['payment-pending'], false),
        },

        {
          title: orderMap['payment-done'].title,
          path: orderMap['payment-done'].path,
          display: orderMap['payment-done'].display,
          count: useGetFilteredOrderCounts(상태_값_매핑['payment-done'], false),
        },
        {
          title: orderMap['discount-offer'].title,
          path: orderMap['discount-offer'].path,
          display: orderMap['discount-offer'].display,
          count: useGetFilteredOrderCounts(상태_값_매핑['payment-done'], true),
        },
        {
          title: orderMap['shipping-pending'].title,
          path: orderMap['shipping-pending'].path,
          display: orderMap['shipping-pending'].display,
          count: useGetFilteredOrderCounts(상태_값_매핑['shipping-pending']),
        },
        {
          title: orderMap['shipping-in-progress'].title,
          path: orderMap['shipping-in-progress'].path,
          display: orderMap['shipping-in-progress'].display,
        },
        {
          title: orderMap['shipping-done'].title,
          path: orderMap['shipping-done'].path,
          display: orderMap['shipping-done'].display,
        },
        {
          title: orderMap['purchase-done'].title,
          path: orderMap['purchase-done'].path,
          display: orderMap['purchase-done'].display,
        },
      ],
    },
  ];

  return {
    orderMenus: orderMenus,
  };
}

function useGetFilteredOrderCounts(status: string, isOffer?: boolean) {
  const shop = useRecoilValue(shopAuthContextState);

  const request: OrderCountsRequest = {
    from: addTimezoneToDate(dayjs().subtract(1, 'month').startOf('day')),
    to: addTimezoneToDate(dayjs().endOf('day')),
    searchPeriodType: 'orderDate',
    orderStatus: [
      'ORDER_STATUS_EXCHANGE-REQUEST', // 교환 요청
      'ORDER_STATUS_ORDER-CANCEL', // 주문 취소
      'ORDER_STATUS_PAYMENT-DONE', // 상품 준비중
      'ORDER_STATUS_PAYMENT-PENDING', // 결제 대기
      'ORDER_STATUS_PURCHASE-DONE', // 구매 확정
      'ORDER_STATUS_RETURN-REQUEST', // 반품 요청
      'ORDER_STATUS_SHIPPING-DONE', // 배송 완료
      'ORDER_STATUS_SHIPPING-IN-PROGRESS', // 배송 중
      'ORDER_STATUS_SHIPPING-PENDING', // 배송 준비 중
    ],
    // 할인 요청 페이지인 경우, 카운트에 중복주문도 포함되어야 함
    isDuplicated: isOffer ? undefined : false,
    isOffer,
  };

  const countList = useGetOrderCounts(shop.shopID, request).data?.orderCounts;
  const count = countList?.find(v => v.orderStatus === status)?.orderCount ?? 0;

  return count;
}

function useGetDuplicatedOrderCounts() {
  const shop = useRecoilValue(shopAuthContextState);

  const request: OrderCountsRequest = {
    from: addTimezoneToDate(dayjs().subtract(1, 'month').startOf('day')),
    to: addTimezoneToDate(dayjs().endOf('day')),
    searchPeriodType: 'orderDate',
    orderStatus: [
      'ORDER_STATUS_EXCHANGE-REQUEST', // 교환 요청
      'ORDER_STATUS_ORDER-CANCEL', // 주문 취소
      'ORDER_STATUS_PAYMENT-DONE', // 상품 준비중
      'ORDER_STATUS_PAYMENT-PENDING', // 결제 대기
      'ORDER_STATUS_PURCHASE-DONE', // 구매 확정
      'ORDER_STATUS_RETURN-REQUEST', // 반품 요청
      'ORDER_STATUS_SHIPPING-DONE', // 배송 완료
      'ORDER_STATUS_SHIPPING-IN-PROGRESS', // 배송 중
      'ORDER_STATUS_SHIPPING-PENDING', // 배송 준비 중
    ],
    isDuplicated: true,
  };

  const countList = useGetOrderCounts(shop.shopID, request).data?.orderCounts;
  const duplicatedCount = sum(countList?.map(v => v.orderCount));

  return duplicatedCount;
}
